/**
 * Solution-name validation — single source of truth (PSTUDIO-3591).
 *
 * The client no longer restricts the character set of a solution name: the user
 * only ever edits the human-readable `displayName`, which is render-only (React
 * JSX auto-escapes it) and is stored by the backend as a plain string. The
 * internal `name`/slug used in composite identifiers and export filenames is
 * generated server-side, not by the user. The only client-side rules that
 * remain are a length bound and uniqueness within the project.
 */
export const MAX_SOLUTION_NAME_LENGTH = 100;

export type SolutionNameValidity = {
  valid: boolean;
  passedLengthCheck: boolean;
  passedUniquenessCheck: boolean;
};

/**
 * Length is measured on the NFC-normalized, trimmed name so a decomposed
 * accented string isn't counted as double, and surrounding whitespace doesn't
 * inflate the count. Valid range is 1..MAX_SOLUTION_NAME_LENGTH inclusive.
 */
export const passesLengthCheck = (name: string): boolean => {
  const trimmed = name.trim().normalize('NFC');

  return trimmed.length > 0 && trimmed.length <= MAX_SOLUTION_NAME_LENGTH;
};

/**
 * Uniqueness compares the raw typed value against the existing names, matching
 * the pre-existing behavior of the validation hooks.
 */
export const checkSolutionName = (
  name: string,
  existingNames: string[]
): SolutionNameValidity => {
  const passedLengthCheck = passesLengthCheck(name);
  const passedUniquenessCheck = !existingNames.includes(name);

  return {
    valid: passedLengthCheck && passedUniquenessCheck,
    passedLengthCheck,
    passedUniquenessCheck,
  };
};
